import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { ReportingWebhook as UIAPIReportingWebhook } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';
import { nullableEpochSecondsToDate } from '../shared/dates';

/**
 * Reporting webhook entity — workspace-scoped outbound reporting webhooks
 * (`/integrations/reporting-webhooks`). Backed by the `/webhooks` resource
 * (model `ReportingWebhook`), NOT `/webhook_configurations` — that's a
 * separate resource.
 *
 * The wire shape uses a numeric id and epoch-second `created_at` /
 * `last_activity_at`. The db layer normalizes the id to a string (cache-key
 * convention) and the timestamps to `Date`, matching layouts / api-credentials.
 *
 * `last_activity_at` can be `null`/absent on the wire for a webhook that has
 * never fired, so it's parsed with `nullableEpochSecondsToDate` (→ `Date(0)`)
 * — a single null row must not throw the whole list parse. `created_at` is
 * always present (a record always has a creation time).
 */
export type Webhook = Omit<UIAPIReportingWebhook, 'id' | 'created_at' | 'last_activity_at'> & {
  id: string;
  created_at: Date;
  last_activity_at: Date;
};

const WebhookFromApi = z
  .looseObject({
    id: z.number().transform(String),
    created_at: epochSecondsToDate,
    last_activity_at: nullableEpochSecondsToDate,
  })
  .pipe(z.custom<Webhook>());

/** @public */
export const webhook = defineEntity<Webhook, Webhook, void, undefined, 'workspace'>({
  name: 'webhook',
  scope: 'workspace',
  pk: (w) => w.id,
  schema: { read: WebhookFromApi },
});

export const webhookKeys = webhook.keys;

export function parseWebhook(raw: UIAPIReportingWebhook): Webhook {
  return webhook.parse(raw);
}
